/**
 * Phone Lookup UI helpers — carrier display, tag hygiene, snippet highlight.
 */

/**
 * Title-case a carrier / operator label (e.g. "orange romania" → "Orange Romania").
 *
 * @param {unknown} value
 * @returns {string}
 */
export function toTitleCaseLabel(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return raw
    .toLowerCase()
    .split(/(\s+|-|\/)/)
    .map((part) => {
      if (/^\s+$/.test(part) || part === '-' || part === '/') return part;
      if (/^(ro|uk|us|eu|gmbh|srl|sa|llc|inc)$/i.test(part)) return part.toUpperCase();
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join('');
}

/**
 * True for raw debug / tech-context chips that should not appear in the metadata card.
 *
 * @param {unknown} tag
 * @returns {boolean}
 */
export function isPhoneDebugNoiseTag(tag) {
  const s = String(tag || '').trim();
  if (!s) return true;
  if (/\braw\s+tech[- ]?context\b/i.test(s)) return true;
  if (/\btech[- ]?context\s+collected\b/i.test(s)) return true;
  if (/^HBF\b/i.test(s)) return true;
  if (/^Sources:\s*/i.test(s)) return true;
  if (/provider\s*sensitive/i.test(s)) return true;
  if (/^[A-Z]{2,6}\s*\([^)]*(raw|debug|tech|internal|collected)[^)]*\)$/i.test(s)) return true;
  if (/^audit[:\s]/i.test(s)) return true;
  if (/^provider_telemetry/i.test(s)) return true;
  return false;
}

/**
 * Humanize a Provider: tag value with Title Case carrier name.
 *
 * @param {unknown} tag
 * @returns {string}
 */
export function formatPhoneDisplayTag(tag) {
  const s = String(tag || '').trim();
  const m = s.match(/^Provider:\s*(.+)$/i);
  if (m) {
    const carrier = toTitleCaseLabel(m[1]);
    return carrier ? `Provider: ${carrier}` : s;
  }
  return s;
}

/**
 * Digits-only phone core for matching (prefer national significant length).
 *
 * @param {unknown} phoneRaw
 * @returns {string}
 */
export function phoneDigitsCore(phoneRaw) {
  let digits = String(phoneRaw || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('00')) digits = digits.slice(2);
  if (digits.length >= 11 && digits.startsWith('40')) return digits.slice(2);
  if (digits.length === 10 && digits.startsWith('0')) return digits.slice(1);
  return digits;
}

/**
 * Build a regex that matches common phone orthographies for the target number.
 *
 * @param {unknown} phoneRaw
 * @returns {RegExp | null}
 */
export function buildPhoneHighlightRegex(phoneRaw) {
  const digits = String(phoneRaw || '').replace(/\D/g, '');
  const core = phoneDigitsCore(phoneRaw);
  if (core.length < 7 && digits.length < 7) return null;
  const primary = (core.length >= 7 ? core : digits).slice(-Math.min(12, Math.max(7, core.length || digits.length)));
  const flexible = primary
    .split('')
    .map((d) => `${d}[\\s.\\-()/]*`)
    .join('')
    .replace(/\[\\s\.\\-\(\)\/\]\*$/, '');
  try {
    return new RegExp(`(?:\\+?\\s*4?\\s*0\\s*)?0?${flexible}`, 'gi');
  } catch {
    return null;
  }
}

/**
 * Split text into highlight segments for the searched phone number.
 *
 * @param {unknown} text
 * @param {unknown} phoneRaw
 * @returns {{ text: string, hit: boolean }[]}
 */
export function splitPhoneHighlightSegments(text, phoneRaw) {
  const source = String(text || '');
  if (!source) return [];
  const re = buildPhoneHighlightRegex(phoneRaw);
  if (!re) return [{ text: source, hit: false }];

  const segments = [];
  let last = 0;
  let match = re.exec(source);
  while (match) {
    if (match.index > last) {
      segments.push({ text: source.slice(last, match.index), hit: false });
    }
    segments.push({ text: match[0], hit: true });
    last = match.index + match[0].length;
    if (match[0].length === 0) {
      re.lastIndex += 1;
    }
    match = re.exec(source);
  }
  if (last < source.length) {
    segments.push({ text: source.slice(last), hit: false });
  }
  return segments.length ? segments : [{ text: source, hit: false }];
}
